import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  Lightbulb,
  Box,
  Play,
  Users,
  Package,
  Wrench,
  Power,
  FileText
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Section, SectionTitle, FadeIn, StaggerContainer, StaggerItem } from '../components/Section';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';

const serviceIcons = [Lightbulb, Box, Play, Users, Package, Wrench, Power, FileText];

export default function Services() {
  const { t, language } = useLanguage();

  const services = {
    en: [
      { title: 'Consulting & Feasibility', description: 'Initial project assessment, site analysis, and feasibility studies to ensure your fountain vision is achievable within budget and technical constraints.', features: ['Site evaluation', 'Budget estimation', 'Technical feasibility', 'Concept recommendations'] },
      { title: 'Fountain Design & 3D Simulation', description: 'Advanced 3D modeling and visualization to bring your fountain concept to life before construction begins.', features: ['3D modeling', 'Visual renderings', 'Animation previews', 'Design iterations'] },
      { title: '3D Show Visualization', description: 'Choreographed fountain show development using advanced 3D visualization and DMX-512 control systems for multimedia water features.', features: ['Show choreography', 'Music synchronization', 'DMX-512 control', 'Pre-visualization'] },
      { title: 'Technical Support for BET/Architects', description: 'Expert technical assistance for engineering firms, architects, and consultants during project study phases.', features: ['Hydraulic calculations', 'System specifications', 'BOQ development', 'Design coordination'] },
      { title: 'Equipment Supply', description: 'Access to premium fountain equipment from leading European manufacturers, carefully selected for your project requirements.', features: ['Pumps & nozzles', 'Lighting systems', 'DMX-512 control', 'Filtration equipment'] },
      { title: 'Installation Assistance', description: 'On-site supervision and technical guidance during the installation phase to ensure proper system assembly.', features: ['Installation supervision', 'Quality control', 'Technical guidance', 'Problem resolution'] },
      { title: 'Commissioning & Startup', description: 'Comprehensive system testing, calibration, and startup assistance to ensure optimal performance from day one.', features: ['System testing', 'Performance tuning', 'Staff training', 'Documentation'] },
      { title: 'Tender & Specification Drafting', description: 'Professional assistance in preparing technical specifications and tender documents for fountain projects.', features: ['Technical specs', 'Tender documents', 'Compliance review', 'Bid evaluation support'] },
    ],
    fr: [
      { title: 'Conseil & Faisabilité', description: "Évaluation initiale du projet, analyse du site et études de faisabilité pour garantir que votre vision de fontaine est réalisable dans le budget et les contraintes techniques.", features: ['Évaluation du site', 'Estimation budgétaire', 'Faisabilité technique', 'Recommandations de concept'] },
      { title: 'Design de Fontaine & Simulation 3D', description: 'Modélisation 3D avancée et visualisation pour donner vie à votre concept de fontaine avant le début de la construction.', features: ['Modélisation 3D', 'Rendus visuels', 'Aperçus animés', 'Itérations de design'] },
      { title: 'Visualisation 3D de Spectacles', description: 'Développement de spectacles de fontaines chorégraphiés avec visualisation 3D avancée et systèmes de contrôle DMX-512 pour les jeux d\'eau multimédia.', features: ['Chorégraphie de spectacle', 'Synchronisation musicale', 'Contrôle DMX-512', 'Pré-visualisation'] },
      { title: 'Support Technique pour BET/Architectes', description: "Assistance technique experte pour les bureaux d'études, architectes et consultants pendant les phases d'études de projet.", features: ['Calculs hydrauliques', 'Spécifications système', 'Développement BOQ', 'Coordination design'] },
      { title: 'Fourniture d\'Équipements', description: 'Accès aux équipements de fontaines premium des fabricants européens leaders, soigneusement sélectionnés pour vos besoins de projet.', features: ['Pompes & ajutages', 'Systèmes d\'éclairage', 'Contrôle DMX-512', 'Équipements de filtration'] },
      { title: 'Assistance à l\'Installation', description: "Supervision sur site et accompagnement technique pendant la phase d'installation pour assurer un assemblage correct du système.", features: ['Supervision d\'installation', 'Contrôle qualité', 'Accompagnement technique', 'Résolution de problèmes'] },
      { title: 'Mise en Service & Démarrage', description: 'Tests complets du système, calibration et assistance au démarrage pour garantir des performances optimales dès le premier jour.', features: ['Tests système', 'Réglage des performances', 'Formation du personnel', 'Documentation'] },
      { title: 'Rédaction de Cahiers des Charges', description: 'Assistance professionnelle dans la préparation des spécifications techniques et documents d\'appel d\'offres pour les projets de fontaines.', features: ['Specs techniques', 'Documents d\'appel d\'offres', 'Revue de conformité', 'Support évaluation des offres'] },
    ],
    es: [
      { title: 'Consultoría & Viabilidad', description: 'Evaluación inicial del proyecto, análisis del sitio y estudios de viabilidad para asegurar que su visión de fuente sea alcanzable dentro del presupuesto y restricciones técnicas.', features: ['Evaluación del sitio', 'Estimación de presupuesto', 'Viabilidad técnica', 'Recomendaciones de concepto'] },
      { title: 'Diseño de Fuentes & Simulación 3D', description: 'Modelado 3D avanzado y visualización para dar vida a su concepto de fuente antes de que comience la construcción.', features: ['Modelado 3D', 'Renderizados visuales', 'Previsualizaciones animadas', 'Iteraciones de diseño'] },
      { title: 'Visualización 3D de Shows', description: 'Desarrollo de shows de fuentes coreografiados con visualización 3D avanzada y sistemas de control DMX-512 para fuentes multimedia.', features: ['Coreografía de show', 'Sincronización musical', 'Control DMX-512', 'Pre-visualización'] },
      { title: 'Soporte Técnico para BET/Arquitectos', description: 'Asistencia técnica experta para oficinas de ingeniería, arquitectos y consultores durante las fases de estudio del proyecto.', features: ['Cálculos hidráulicos', 'Especificaciones del sistema', 'Desarrollo de BOQ', 'Coordinación de diseño'] },
      { title: 'Suministro de Equipos', description: 'Acceso a equipos de fuentes premium de fabricantes europeos líderes, cuidadosamente seleccionados para los requisitos de su proyecto.', features: ['Bombas y boquillas', 'Sistemas de iluminación', 'Control DMX-512', 'Equipos de filtración'] },
      { title: 'Asistencia en Instalación', description: 'Supervisión en sitio y orientación técnica durante la fase de instalación para asegurar el ensamblaje correcto del sistema.', features: ['Supervisión de instalación', 'Control de calidad', 'Orientación técnica', 'Resolución de problemas'] },
      { title: 'Puesta en Marcha & Arranque', description: 'Pruebas completas del sistema, calibración y asistencia de arranque para asegurar un rendimiento óptimo desde el primer día.', features: ['Pruebas del sistema', 'Ajuste de rendimiento', 'Capacitación del personal', 'Documentación'] },
      { title: 'Redacción de Pliegos & Especificaciones', description: 'Asistencia profesional en la preparación de especificaciones técnicas y documentos de licitación para proyectos de fuentes.', features: ['Especificaciones técnicas', 'Documentos de licitación', 'Revisión de cumplimiento', 'Soporte evaluación de ofertas'] },
    ],
  };

  const currentServices = services[language] || services.en;

  const whyPoints = {
    en: [
      { title: 'Single Point of Contact', desc: 'One partner for all your fountain project needs, from concept to commissioning.' },
      { title: 'European Quality Standards', desc: "Access to premium equipment and engineering practices from Europe's leading manufacturers." },
      { title: 'International Market Experience', desc: 'Deep understanding of executing complex projects across diverse international contexts.' },
    ],
    fr: [
      { title: 'Point de Contact Unique', desc: 'Un partenaire pour tous vos besoins de projet de fontaine, du concept à la mise en service.' },
      { title: 'Standards de Qualité Européens', desc: "Accès aux équipements premium et pratiques d'ingénierie des principaux fabricants européens." },
      { title: 'Expérience du Marché International', desc: "Compréhension approfondie de l'exécution de projets complexes dans divers contextes internationaux." },
    ],
    es: [
      { title: 'Punto de Contacto Único', desc: 'Un socio para todas sus necesidades de proyecto de fuente, desde el concepto hasta la puesta en marcha.' },
      { title: 'Estándares de Calidad Europeos', desc: 'Acceso a equipos premium y prácticas de ingeniería de los principales fabricantes europeos.' },
      { title: 'Experiencia en Mercado Internacional', desc: 'Comprensión profunda de la ejecución de proyectos complejos en diversos contextos internacionales.' },
    ],
  };

  const currentWhyPoints = whyPoints[language] || whyPoints.en;

  return (
    <main data-testid="services-page">
      <SEO page="services" />
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 hero-gradient overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-10 right-20 w-80 h-80 rounded-full bg-[#29ABE2]/5 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="text-sm font-medium uppercase tracking-widest text-[#29ABE2] mb-4 block">
              {t('servicesPage.eyebrow')}
            </span>
            <h1 className="text-5xl lg:text-6xl font-bold text-[#1A1A1A] tracking-tight uppercase mb-6">
              {t('servicesPage.title')}
            </h1>
            <p className="text-xl text-[#6C757D] leading-relaxed">
              {t('servicesPage.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Grid */}
      <Section background="white">
        <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {currentServices.map((service, index) => {
            const Icon = serviceIcons[index] || Lightbulb;
            return (
              <StaggerItem key={index}>
                <div className="service-card bg-white border border-gray-100 p-8 h-full group">
                  <div className="flex items-start gap-6">
                    <div className="w-14 h-14 bg-[#29ABE2]/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-[#29ABE2]/20 transition-colors">
                      <Icon className="w-7 h-7 text-[#29ABE2]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-bold text-[#1A1A1A] mb-3 uppercase tracking-wide">
                        {service.title}
                      </h3>
                      <p className="text-sm text-[#6C757D] leading-relaxed mb-4">
                        {service.description}
                      </p>
                      <ul className="grid grid-cols-2 gap-2">
                        {service.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm text-[#1A1A1A]">
                            <span className="w-1.5 h-1.5 bg-[#29ABE2] rounded-full" />
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </StaggerItem>
            );
          })}
        </StaggerContainer>
      </Section>

      {/* Why Choose Us */}
      <Section background="surface">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <FadeIn>
            <SectionTitle
              eyebrow={t('servicesPage.whyChooseEyebrow')}
              title={t('servicesPage.whyChooseTitle')}
              description={t('servicesPage.whyChooseDescription')}
            />
            <div className="space-y-4 mt-8">
              {currentWhyPoints.map((point, index) => (
                <div key={index} className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#29ABE2] text-white rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                    {index + 1}
                  </div>
                  <div>
                    <h4 className="font-bold text-[#1A1A1A] mb-1">{point.title}</h4>
                    <p className="text-sm text-[#6C757D]">{point.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </FadeIn>
          <FadeIn delay={0.2}>
            <img
              src="https://images.unsplash.com/photo-1630386519709-c33c5041b948?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwyfHx3YXRlciUyMGZlYXR1cmV8ZW58MHx8fHwxNzczODU2MzEwfDA&ixlib=rb-4.1.0&q=85"
              alt="Fountain engineering"
              className="w-full aspect-square object-cover rounded-sm"
            />
          </FadeIn>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="dark" paddingY="py-20 lg:py-28">
        <div className="text-center max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="text-4xl lg:text-5xl font-bold text-white tracking-tight uppercase mb-6">
              {t('servicesPage.needSupport')}
            </h2>
            <p className="text-lg text-gray-400 mb-8">
              {t('servicesPage.needSupportDesc')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  data-testid="services-cta-btn"
                  className="bg-[#29ABE2] text-white hover:bg-[#0077B6] rounded-none px-8 py-6 text-sm font-bold uppercase tracking-wider"
                >
                  {t('servicesPage.discussRequirements')}
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/consultants">
                <Button
                  data-testid="services-consultants-btn"
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-[#1A1A1A] rounded-none px-8 py-6 text-sm font-bold uppercase tracking-wider"
                >
                  {t('servicesPage.forConsultants')}
                </Button>
              </Link>
            </div>
          </FadeIn>
        </div>
      </Section>
    </main>
  );
}
